import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  fetchExchangeRates,
  getCachedRates,
  cacheRates,
  convertCurrency,
  getCurrencySymbol as getSymbol
} from '../services/currencyService';

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Load saved currency preference from localStorage
    return localStorage.getItem('preferredCurrency') || 'GBP';
  });

  const [rates, setRates] = useState({ USD: 1.27, EUR: 1.17 }); // Default fallback rates
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load exchange rates
  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      // Try to get cached rates first
      const cached = getCachedRates();
      if (cached) {
        setRates(cached.rates);
        setLastUpdated(cached.timestamp);
        setLoading(false);
        return;
      }

      // Fetch fresh rates from API
      const newRates = await fetchExchangeRates();
      setRates(newRates);
      setLastUpdated(Date.now());

      // Cache the rates
      cacheRates(newRates);
    } catch (error) {
      console.error('Failed to load exchange rates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load rates on mount
  useEffect(() => {
    loadRates();

    // Refresh rates every hour
    const interval = setInterval(loadRates, 3600000); // 1 hour

    return () => clearInterval(interval);
  }, [loadRates]);

  // Save currency preference when it changes
  useEffect(() => {
    localStorage.setItem('preferredCurrency', currency);
    console.log(`💱 Currency preference saved: ${currency}`);
  }, [currency]);

  /**
   * Convert amount from GBP to the currently selected currency
   * @param {number} amountInGBP Amount in GBP
   * @returns {number} Converted amount
   */
  const convertFromGBP = useCallback((amountInGBP) => {
    return convertCurrency(amountInGBP, currency, rates);
  }, [currency, rates]);

  /**
   * Get the symbol for the currently selected currency
   * @returns {string} Currency symbol (£, $, or €)
   */
  const getCurrencySymbol = useCallback(() => {
    return getSymbol(currency);
  }, [currency]);

  /**
   * Format amount with currency symbol
   * @param {number} amount Amount to format
   * @returns {string} Formatted currency string (e.g., "£75", "$95")
   */
  const formatAmount = useCallback((amount) => {
    const symbol = getCurrencySymbol();
    return `${symbol}${amount.toFixed(0)}`;
  }, [getCurrencySymbol]);

  /**
   * Manually refresh exchange rates
   */
  const refreshRates = useCallback(async () => {
    console.log('🔄 Manually refreshing exchange rates...');
    await loadRates();
  }, [loadRates]);

  const value = {
    currency,
    setCurrency,
    rates,
    loading,
    lastUpdated,
    convertFromGBP,
    getCurrencySymbol,
    formatAmount,
    refreshRates
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Hook to use currency context
 * @returns {CurrencyContextValue}
 */
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
